import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	FormControlLabel,
	Stack,
	Switch,
	Typography,
} from '@mui/material';
import Modal from './Modal';
import { buildCardFeeNotice, formatCurrency } from '../../Shared/stripeFee';
import { useStripePaymentsEnabled } from '../../Context/AppContext';

const SendInvoiceModal = ({ open, onClose, bid, onSend, sending = false }) => {
	const stripePaymentsEnabled = useStripePaymentsEnabled();
	const quoteIncludesFee = Boolean(bid?.quote_includes_stripe_fee);
	const [includeCardFeeNotice, setIncludeCardFeeNotice] =
		useState(!quoteIncludesFee);

	useEffect(() => {
		if (open) {
			setIncludeCardFeeNotice(!quoteIncludesFee);
		}
	}, [open, quoteIncludesFee]);

	const noticePreview = useMemo(() => {
		if (!bid?.amount) {
			return '';
		}
		return buildCardFeeNotice(bid.amount);
	}, [bid?.amount]);

	const handleSend = () => {
		onSend({
			include_card_fee_notice:
				stripePaymentsEnabled && !quoteIncludesFee
					? includeCardFeeNotice
					: false,
		});
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			modalTitle="Send invoice"
			maxWidth="sm"
			alwaysMaxWidth
			useActionButtons
			actionButtons={
				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button onClick={onClose} disabled={sending}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleSend} disabled={sending}>
						{sending ? 'Sending...' : 'Send invoice'}
					</Button>
				</Box>
			}
		>
			<Stack spacing={2.5} sx={{ py: 1 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 2,
						flexWrap: 'wrap',
					}}
				>
					<Box>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
							Invoice amount
						</Typography>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							${formatCurrency(bid?.amount || 0)}
						</Typography>
					</Box>
					{stripePaymentsEnabled ? (
						<Chip
							size="small"
							label={
								quoteIncludesFee
									? 'Quote included card fees'
									: 'Quote did not include card fees'
							}
							color={quoteIncludesFee ? 'success' : 'default'}
							variant={quoteIncludesFee ? 'filled' : 'outlined'}
							sx={{ flexShrink: 0 }}
						/>
					) : null}
				</Box>

				{stripePaymentsEnabled && !quoteIncludesFee ? (
					<>
						<FormControlLabel
							control={
								<Switch
									checked={includeCardFeeNotice}
									onChange={(e) => setIncludeCardFeeNotice(e.target.checked)}
									disabled={sending}
								/>
							}
							label="Include card fee notice on invoice"
						/>

						{includeCardFeeNotice ? (
							<Alert severity="info" sx={{ alignItems: 'flex-start' }}>
								<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
									Customer will see:
								</Typography>
								<Typography variant="body2">{noticePreview}</Typography>
							</Alert>
						) : (
							<Typography variant="body2" color="text.secondary">
								No card fee notice will be added to the invoice email or PDF.
							</Typography>
						)}
					</>
				) : null}
			</Stack>
		</Modal>
	);
};

export default SendInvoiceModal;
